/**
* PURPOSE: Modal para administrar organizaciones habilitadas por usuario.
* RESPONSIBILITIES: Lista, agrega y elimina permisos en menu_user_company sin tocar datos base del usuario.
* COLLABORATORS: src/services/company.js, src/sections/UserSection.jsx, backend controllers/Menu_Permisos.js
* ROUTE: src/admin/CreateUser/Modal/ModalUpdatePermisosCompany/ModalUpdatePermisosCompany.jsx
*/
import React, { useEffect, useMemo, useState } from "react";
import Modal from "@mui/joy/Modal";
import Typography from "@mui/joy/Typography";
import Select from "react-select";
import { toast } from "react-hot-toast";
import { MdDelete } from "react-icons/md";
import {
  createMenuCompany,
  deleteMenuCompany,
  getCompanies,
  getMenuCompanies,
} from "../../../../services/company.js";

const isOneCompanyUser = (user) => {
  return user?.is_one_company === true || Number(user?.is_one_company) === 1;
};

const ModalUpdatePermisosCompany = ({ open, setOpen, user }) => {
  const [allCompanies, setAllCompanies] = useState([]);
  const [enabledCompanies, setEnabledCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);

  const oneCompany = isOneCompanyUser(user);
  const role = Number(user?.rol);

  const companyOptions = useMemo(() => {
    const enabledIds = new Set(enabledCompanies.map((company) => Number(company.idcompany)));

    return allCompanies
      .filter((company) => !enabledIds.has(Number(company.idcompany)))
      .map((company) => ({
        value: company.idcompany,
        label: `${company.name || company.abrev || "Organización"}${company.abrev ? ` (${company.abrev})` : ""}`,
      }));
  }, [allCompanies, enabledCompanies]);

  const loadData = async () => {
    if (!user?.iduser) return;

    try {
      setLoading(true);
      const [companies, menuCompanies] = await Promise.all([
        getCompanies(),
        getMenuCompanies({ iduser: user.iduser }),
      ]);

      setAllCompanies(Array.isArray(companies) ? companies : []);
      setEnabledCompanies(Array.isArray(menuCompanies) ? menuCompanies : []);
    } catch (error) {
      console.error("Error al cargar permisos de compañías:", error);
      toast.error("No se pudo cargar permisos de organizaciones.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && user?.iduser) loadData();
  }, [open, user?.iduser]);

  const handleAddCompany = async () => {
    if (!selectedCompany?.value) {
      toast.error("Selecciona una organización.");
      return;
    }

    try {
      await createMenuCompany({
        iduser: user.iduser,
        idcompany: selectedCompany.value,
      });

      toast.success("Organización habilitada correctamente.");
      setSelectedCompany(null);
      await loadData();
    } catch (error) {
      console.error("Error al habilitar organización:", error);
      toast.error(error?.response?.data?.msg || "No se pudo habilitar la organización.");
    }
  };

  const handleDeleteCompany = async (idcompany) => {
    try {
      await deleteMenuCompany({
        iduser: user.iduser,
        idcompany,
      });

      toast.success("Organización deshabilitada correctamente.");
      await loadData();
    } catch (error) {
      console.error("Error al deshabilitar organización:", error);
      toast.error(error?.response?.data?.msg || "No se pudo deshabilitar la organización.");
    }
  };

  const shouldShowAdminAllNotice = role === 1 && !oneCompany;

  return (
    <Modal
      aria-labelledby="modal-title"
      aria-describedby="modal-desc"
      open={open}
      onClose={() => setOpen(false)}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <div className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-lg">
        <button
          className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 focus:outline-none"
          onClick={() => setOpen(false)}
        >
          ✖
        </button>

        <Typography component="h2" id="modal-title" className="mb-4 text-center text-xl font-bold text-gray-800">
          Permisos de Organizaciones
        </Typography>

        <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          <p>
            <strong>Usuario:</strong> {user?.username} · <strong>Rol:</strong> {user?.rol}
          </p>
          <p className="mt-1">
            <strong>Modo:</strong> {oneCompany ? `Una compañía principal (${user?.idcompany || "sin id"})` : "Múltiples compañías"}
          </p>
          {oneCompany ? (
            <p className="mt-2 text-amber-700">
              Estos permisos se usarán cuando cambies <strong>is_one_company</strong> a false.
            </p>
          ) : null}
          {shouldShowAdminAllNotice ? (
            <p className="mt-2 text-blue-700">
              Admin multi-company tiene acceso a todas las organizaciones. No necesita permisos específicos.
            </p>
          ) : null}
        </div>

        {!shouldShowAdminAllNotice ? (
          <div className="mb-5 flex flex-col gap-3 md:flex-row">
            <div className="flex-1">
              <Select
                options={companyOptions}
                value={selectedCompany}
                onChange={setSelectedCompany}
                placeholder="Selecciona una organización"
                isDisabled={loading}
              />
            </div>
            <button
              className="rounded bg-purple-600 px-4 py-2 text-white shadow hover:bg-purple-700 disabled:opacity-60"
              onClick={handleAddCompany}
              disabled={loading}
            >
              Agregar
            </button>
          </div>
        ) : null}

        <div>
          <h3 className="mb-2 text-lg font-semibold text-gray-700">Organizaciones habilitadas</h3>

          {loading ? <p className="text-sm text-gray-500">Cargando...</p> : null}

          {!loading && enabledCompanies.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500">
              Este usuario no tiene organizaciones habilitadas en menu_user_company.
            </div>
          ) : null}

          {!loading && enabledCompanies.length > 0 ? (
            <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
              {enabledCompanies.map((company) => (
                <div key={company.idcompany} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-800">
                      {company.name || company.abrev || `Company ${company.idcompany}`}
                    </p>
                    <p className="text-xs text-slate-500">
                      ID: {company.idcompany} {company.abrev ? `· ${company.abrev}` : ""}
                    </p>
                  </div>

                  {!shouldShowAdminAllNotice ? (
                    <button
                      className="rounded-full p-2 text-red-500 hover:bg-red-50"
                      onClick={() => handleDeleteCompany(company.idcompany)}
                      title="Quitar organización"
                    >
                      <MdDelete />
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default ModalUpdatePermisosCompany;
